import { AxiosError, AxiosResponse } from 'axios';
import { push } from 'connected-react-router';

import { appConfig } from '../../config';

import * as NotificationActionsTypes from './types';
import {
    AddNotification,
    REMOVE_NOTIFICATION,
    REMOVE_NOTIFICATION_INFO,
    RemoveNotification,
    RemoveNotificationInfo,
    ShowNotificationInfo
} from './types';
import { NotificationInfo, SysaElement } from './state';
import { AppDispatch } from '../index';
import { log } from '../../shared/utils/log';
import i18n from '../../../i18n';
import { http } from '../../api/axios';

export const TYPE_INFO = 'info';
export const TYPE_WARNING = 'warning';
export const TYPE_ERROR = 'error';

export interface RuntimeDuplicationError {
    runtimeDuplication?: boolean;
    response: AxiosResponse;
    error: AxiosResponse;
}

export type ErrorResponse = AxiosError | AxiosResponse | RuntimeDuplicationError | string;

export function isErrorOfType(error: any): error is ErrorResponse {
    if (typeof error === 'string') {
        return true;
    } else if (error && error.isAxiosError) {
        return true;
    } else if (error && error.status && error.data) {
        return true;
    } else if (error && error.runtimeDuplication) {
        return true;
    }
    return false;
}

let nextId = 0;

function addNotification(id: number, text: string | null, type = TYPE_INFO): AddNotification {
    return {
        type: NotificationActionsTypes.ADD_NOTIFICATION,
        payload: {
            id,
            text,
            type
        } as SysaElement
    };
}

export const removeNotification = (id: number): RemoveNotification => {
    return { type: REMOVE_NOTIFICATION, payload: id };
};

export function showNotification(text: string | null = null, type = TYPE_INFO, milliseconds = 5000) {
    return (dispatch: AppDispatch) => {
        if (!text) {
            return;
        }

        nextId = nextId + 1;
        dispatch(addNotification(nextId, text, type));
        if (type !== TYPE_ERROR) {
            setRemoveTimeout(dispatch, nextId, milliseconds);
        }
    };
}

/* provides a notification with 3 specific fields: title, dataDisplay and a info message*/
export function showNotificationWithInfo(
    title: string | null,
    message: string | null,
    dataDisplay?: string | null,
    dataRaw?: string | null,
    callback?: Function | null,
    showCancel?: boolean,
    submitLabel: string = 'OK'
): ShowNotificationInfo {
    return {
        type: NotificationActionsTypes.SHOW_NOTIFICATION_INFO,
        payload: { title, message, dataDisplay, dataRaw, callback, showCancel, submitLabel } as NotificationInfo
    };
}

export const removeNotificationWithInfo = (): RemoveNotificationInfo => {
    return { type: REMOVE_NOTIFICATION_INFO };
};

export function globalError(errorResponse: ErrorResponse, displayNotification?: boolean) {
    return (dispatch: AppDispatch) => globalWarningOrError(errorResponse, TYPE_ERROR, dispatch, displayNotification);
}

export function globalWarning(errorResponse: ErrorResponse, displayNotification?: boolean) {
    return (dispatch: AppDispatch) => globalWarningOrError(errorResponse, TYPE_WARNING, dispatch, displayNotification);
}

export function globalErrorResponse(errorResponse: ErrorResponse, displayNotification?: boolean) {
    let status = 999;
    if ((errorResponse as AxiosError).response) {
        status = (errorResponse as AxiosError).response?.status ?? 999;
    }
    if ((errorResponse as AxiosResponse).status) {
        status = (errorResponse as AxiosResponse).status;
    }

    const type = status >= 400 && status <= 499 ? TYPE_WARNING : TYPE_ERROR;
    const showNotification = type === TYPE_WARNING ? displayNotification : true;
    return (dispatch: AppDispatch) => globalWarningOrError(errorResponse, type, dispatch, showNotification);
}

function globalWarningOrError(errorResponse: ErrorResponse, type, dispatch, displayNotification = true) {
    if ((errorResponse as RuntimeDuplicationError).runtimeDuplication) {
        return;
    }

    let msg = '';
    let status = 0;
    if ((errorResponse as AxiosError).response) {
        const resp = (errorResponse as AxiosError).response as AxiosResponse;
        msg = resp.data ? resp.data.message || i18n.t('notifications.globalErrorResponse.errorMessage') : resp;
        status = resp.status;
    } else if ((errorResponse as AxiosResponse).status) {
        const resp = errorResponse as AxiosResponse;
        msg = resp.data ? resp.data.message || i18n.t('notifications.globalErrorResponse.errorMessage') : resp;
        status = resp.status;
    } else {
        msg = errorResponse as string;
    }

    if (type === TYPE_ERROR) {
        log.warning(msg);
        msg = i18n.t('notifications.globalErrorResponse.tryAgain', { msg });
    } else {
        log.warning(msg);
    }

    if (status >= 500) {
        http.get(appConfig().endpointHealth)
            .then(() => dispatch(showNotification(msg, TYPE_ERROR)))
            .catch(() => dispatch(push('/maintenance')));
    } else if (displayNotification) {
        dispatch(showNotification(msg, type));
    }
}

function setRemoveTimeout(dispatch: AppDispatch, id: number, milliseconds: number) {
    window.setTimeout(() => dispatch(removeNotification(id)), milliseconds);
}
